<template>
  <div class="history-container" dir="rtl">
    <!-- Header -->
    <div class="header">
      <div class="header-text">
        <h2>📋 تاریخچه درخواست‌ها</h2>
        <div class="subtitle-section">
          <p>مشاهده آخرین دستورات ارسال شده به دستگاه</p>
          <button
              class="refresh-btn"
              :disabled="isLoading"
              @click="loadHistory"
          >
            <span v-if="!isLoading">🔄 بروزرسانی</span>
            <span v-else>⏳ بارگذاری...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-row">
        <div class="filter-item">
          <label>نوع دستور:</label>
          <select
              v-model="selectedCommandType"
              class="filter-select"
              @change="applyFilters"
          >
            <option value="">همه دستورات</option>
            <option value="SOS">شماره اضطراری</option>
            <option value="SENALM">هشدار لرزشی</option>
            <option value="POWERALM">هشدار قطع برق</option>
            <option value="BATALM">هشدار باطری</option>
            <option value="ACCALM">هشدار سوئیچ</option>
            <option value="MOVING">هشدار جابجایی</option>
            <option value="SPEED">هشدار سرعت</option>
            <option value="LEVEL">حساسیت سنسور</option>
            <option value="RESET">راه اندازی مجدد</option>
            <option value="powerOff">قطع برق</option>
            <option value="powerOn">وصل برق</option>
            <option value="CENTER">شماره مدیر</option>
            <option value="SERVER">تنظیم سرور</option>
          </select>
        </div>

        <div class="filter-item">
          <label>تاریخ:</label>
          <select
              v-model="selectedDateRange"
              class="filter-select"
              @change="applyFilters"
          >
            <option value="today">امروز</option>
            <option value="week">هفته گذشته</option>
            <option value="month">ماه گذشته</option>
            <option value="all">همه زمان‌ها</option>
          </select>
        </div>
      </div>

      <div class="filter-actions" v-if="hasActiveFilters">
        <button class="clear-filters-btn" @click="clearFilters">
          🗑️ پاک کردن فیلترها
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-section">
      <div class="loading-spinner">⏳</div>
      <p>در حال بارگذاری تاریخچه...</p>
    </div>

    <!-- History List -->
    <div v-else-if="filteredHistory.length > 0" class="history-list">
      <div
          v-for="item in paginatedHistory"
          :key="item.id"
          class="history-item"
      >
        <div class="item-header">
          <div class="command-info">
            <span class="command-icon">{{ item.icon }}</span>
            <div class="command-details">
              <h4 class="command-title">{{ item.typeText }}</h4>
              <p class="command-text">{{ item.commandName }}</p>
            </div>
          </div>
          <div class="status-section">
            <span :class="['status-badge', item.status]">
              {{ item.statusText }}
            </span>
            <span class="timestamp">{{ item.relativeTime }}</span>
          </div>
        </div>

        <!-- Details (Expandable) -->
        <div v-if="item.expanded" class="item-details">
          <div class="detail-grid">
            <div class="detail-item">
              <label>دستور کامل:</label>
              <code>{{ item.commandName }}</code>
            </div>
            <div v-if="item.response" class="detail-item">
              <label>پاسخ سرور:</label>
              <span>{{ item.response }}</span>
            </div>
            <div v-if="item.error" class="detail-item">
              <label>خطا:</label>
              <span class="error-text">{{ item.error }}</span>
            </div>
            <div class="detail-item">
              <label>زمان دقیق:</label>
              <span>{{ item.formattedDate }}</span>
            </div>
            <div v-if="item.executedAt" class="detail-item">
              <label>زمان اجرا:</label>
              <span>{{ formatDate(item.executedAt) }}</span>
            </div>
            <div v-if="item.executionTime" class="detail-item">
              <label>مدت زمان اجرا:</label>
              <span>{{ item.executionTime }} میلی‌ثانیه</span>
            </div>
          </div>

          <!-- Retry Button for Failed Commands -->
          <div v-if="item.canRetry" class="action-buttons">
            <button
                class="retry-btn"
                :disabled="isRetrying"
                @click="retryCommand(item)"
            >
              <span v-if="!isRetrying">🔄 تلاش مجدد</span>
              <span v-else>⏳ در حال تلاش...</span>
            </button>
          </div>
        </div>

        <!-- Toggle Button -->
        <button class="toggle-details" @click="toggleItemDetails(item)">
          {{ item.expanded ? 'بستن جزئیات ▲' : 'مشاهده جزئیات ▼' }}
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!isLoading" class="empty-state">
      <div class="empty-icon">📭</div>
      <h3>تاریخچه‌ای یافت نشد</h3>
      <p v-if="hasActiveFilters">
        هیچ درخواستی با فیلترهای انتخابی یافت نشد. فیلترها را تغییر دهید.
      </p>
      <p v-else>هنوز هیچ دستوری ارسال نشده است.</p>
      <button
          v-if="hasActiveFilters"
          class="clear-filters-btn"
          @click="clearFilters"
      >
        پاک کردن فیلترها
      </button>
    </div>

    <!-- Pagination -->
    <div v-if="filteredHistory.length > itemsPerPage" class="pagination">
      <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
      >
        ◀ قبلی
      </button>

      <div class="page-numbers">
        <button
            v-for="page in visiblePages"
            :key="page"
            :class="['page-number', { active: page === currentPage }]"
            @click="changePage(page)"
        >
          {{ page }}
        </button>
      </div>

      <span class="page-info">
        صفحه {{ currentPage }} از {{ totalPages }}
        ({{ filteredHistory.length }} مورد)
      </span>

      <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
      >
        بعدی ▶
      </button>
    </div>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import CommandHistoryService from '@/services/command-history.service'

export default {
  name: 'CommandHistory',
  props: {
    selectedImei: {
      type: String,
      required: true,
    },
    deviceInfo: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  data() {
    return {
      history: [],
      filteredHistory: [],
      isLoading: false,
      isRetrying: false,
      currentPage: 1,
      itemsPerPage: 10,

      // Filters
      selectedCommandType: '',
      selectedDateRange: 'week',
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.filteredHistory.length / this.itemsPerPage)
    },

    paginatedHistory() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredHistory.slice(start, end)
    },

    hasActiveFilters() {
      return this.selectedCommandType
          || this.selectedDateRange !== 'all'
    },

    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const delta = 2

      const range = []
      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        range.unshift('...')
      }
      if (current + delta < total - 1) {
        range.push('...')
      }

      range.unshift(1)
      if (total > 1) {
        range.push(total)
      }

      return range.filter((v, i, arr) => arr.indexOf(v) === i)
    },
  },
  watch: {
    selectedImei: {
      handler(newVal) {
        if (newVal) {
          this.loadHistory()
        }
      },
      immediate: true,
    },
  },
  methods: {
    async loadHistory() {
      if (!this.selectedImei) return

      this.isLoading = true
      try {
        const response = await CommandHistoryService.getFilteredHistory({
          imei: this.selectedImei,
          limit: 100,
          offset: 0,
        })

        if (response.status === 'success') {
          const historyData = response.result?.data || response.data?.data || response.data || []
          this.history = CommandHistoryService.formatHistoryData(historyData)
          this.applyFilters()
        } else {
          throw new Error(response.message || 'خطا در بارگذاری تاریخچه')
        }
      } catch (error) {
        // console.error('خطا در بارگذاری تاریخچه:', error)
        this.showToast('خطا در بارگذاری تاریخچه درخواست‌ها', 'error')
        this.history = []
        this.filteredHistory = []
      } finally {
        this.isLoading = false
      }
    },

    applyFilters() {
      let filtered = [...this.history]

      if (this.selectedCommandType) {
        filtered = CommandHistoryService.filterByCommandType(filtered, this.selectedCommandType)
      }

      if (this.selectedDateRange !== 'all') {
        const now = new Date()
        let fromDate

        switch (this.selectedDateRange) {
          case 'today':
            fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            break
          case 'week':
            fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            break
          case 'month':
            fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            break
        }

        if (fromDate) {
          filtered = filtered.filter(item => new Date(item.createdAt) >= fromDate)
        }
      }

      this.filteredHistory = CommandHistoryService.sortHistory(filtered, 'createdAt', 'desc')
      this.currentPage = 1
    },

    clearFilters() {
      this.selectedCommandType = ''
      this.selectedDateRange = 'all'
      this.applyFilters()
    },

    toggleItemDetails(item) {
      this.$set(item, 'expanded', !item.expanded)
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== '...') {
        this.currentPage = page
      }
    },

    async retryCommand(item) {
      this.isRetrying = true
      try {
        const response = await CommandHistoryService.retryCommand({
          imei: this.selectedImei,
          historyId: item.id,
        })

        if (response.status === 'success') {
          this.showToast('دستور مجدداً ارسال شد', 'success')
          setTimeout(() => {
            this.loadHistory()
          }, 1000)
        }
      } catch (error) {
        this.showToast('خطا در ارسال مجدد دستور', 'error')
      } finally {
        this.isRetrying = false
      }
    },

    formatDate(dateString) {
      return CommandHistoryService.formatDate(dateString)
    },

    showToast(message, type = 'info') {
      const variants = {
        success: 'success',
        error: 'danger',
        warning: 'warning',
        info: 'info',
      }

      const icons = {
        success: 'CheckIcon',
        error: 'XIcon',
        warning: 'AlertTriangleIcon',
        info: 'InfoIcon',
      }

      const titles = {
        success: 'موفقیت',
        error: 'خطا',
        warning: 'هشدار',
        info: 'اطلاع',
      }

      try {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: titles[type] || 'اطلاع',
            text: message,
            icon: icons[type] || 'InfoIcon',
            variant: variants[type] || 'info',
          },
        })
      } catch (error) {
        alert(`${titles[type]}: ${message}`)
      }
    },
  },
}
</script>

<style scoped>
.history-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Vazir', sans-serif;
  direction: rtl;
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Header Styles */
.header {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-main h2 {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.header-main p {
  margin: 0;
  color: #6c757d;
  font-size: 0.95rem;
}

.refresh-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(79, 172, 254, 0.3);
  position: relative;
  overflow: hidden;
}

.refresh-toggle::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.refresh-toggle:hover:not(:disabled)::before {
  left: 100%;
}

.refresh-toggle:hover:not(:disabled) {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 172, 254, 0.4);
}

.refresh-toggle:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(79, 172, 254, 0.3);
}

.refresh-toggle:disabled {
  background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(149, 165, 166, 0.2);
}

.refresh-text {
  font-weight: 600;
  letter-spacing: 0.3px;
}

.refresh-icon {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.refresh-toggle:hover:not(:disabled) .refresh-icon {
  transform: rotate(180deg);
}

/* Filters Section */
.filters-section {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.filter-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 16px;
}

.search-item {
  max-width: 200px;
}

.filter-item label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #495057;
  font-size: 0.9rem;
}

.filter-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 0.9rem;
  background: #ffffff;
  color: #495057;
  transition: border-color 0.2s ease;
  font-family: inherit;
}

.filter-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
}

.clear-filters-btn {
  background: #6c757d;
  border: 1px solid #6c757d;
  border-radius: 8px;
  padding: 8px 16px;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-filters-btn:hover {
  background: #545b62;
  border-color: #545b62;
}

/* Loading Section */
.loading-section {
  text-align: center;
  padding: 60px 20px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  font-size: 2rem;
  margin-bottom: 16px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* History List */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.history-item {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.history-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-color: #dee2e6;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
}

.command-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.command-icon {
  font-size: 1.5rem;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 50%;
}

.command-details h4 {
  margin: 0 0 4px 0;
  font-size: 1rem;
  color: #333;
  font-weight: 600;
}

.command-text {
  margin: 0;
  font-size: 0.85rem;
  color: #6c757d;
  font-family: 'Courier New', monospace;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  padding: 6px 10px;
  border-radius: 6px;
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  border: 1px solid;
}

.status-badge.success {
  background: #d4edda;
  color: #155724;
  border-color: #c3e6cb;
}

.status-badge.failed {
  background: #f8d7da;
  color: #721c24;
  border-color: #f5c6cb;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
  border-color: #ffeaa7;
}

.timestamp {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Item Details */
.item-details {
  background: #f8f9fa;
  padding: 20px;
  border-top: 1px solid #e9ecef;
}

.detail-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item label {
  font-weight: 500;
  color: #495057;
  font-size: 0.9rem;
}

.detail-item span {
  color: #6c757d;
  font-size: 0.9rem;
}

.detail-item code {
  background: #ffffff;
  border: 1px solid #e9ecef;
  padding: 10px 12px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: #495057;
  word-break: break-all;
}

.error-text {
  color: #dc3545 !important;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-start;
}

.retry-btn {
  background: #ffc107;
  border: 1px solid #ffc107;
  color: #212529;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover:not(:disabled) {
  background: #e0a800;
  border-color: #e0a800;
}

.retry-btn:disabled {
  background: #6c757d;
  border-color: #6c757d;
  color: white;
  cursor: not-allowed;
}

.toggle-details {
  width: 100%;
  padding: 12px;
  background: #f8f9fa;
  border: none;
  border-top: 1px solid #e9ecef;
  color: #007bff;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.toggle-details:hover {
  background: #e9ecef;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 16px;
}

.empty-state h3 {
  color: #495057;
  margin-bottom: 8px;
  font-weight: 600;
}

.empty-state p {
  color: #6c757d;
  margin: 8px 0;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-top: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-numbers {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-number {
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background: #ffffff;
  color: #495057;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  text-align: center;
}

.page-number:hover {
  background: #e9ecef;
  border-color: #dee2e6;
}

.page-number.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.page-btn {
  padding: 10px 16px;
  background: #007bff;
  color: white;
  border: 1px solid #007bff;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  background: #0056b3;
  border-color: #0056b3;
}

.page-btn:disabled {
  background: #6c757d;
  border-color: #6c757d;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.9rem;
  color: #6c757d;
  white-space: nowrap;
}

[dir=rtl] .header-main h2 {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .header-main p {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .refresh-text {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .timestamp {
  direction: rtl;
  text-align: right;
}

[dir=rtl] .command-text {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .detail-item span {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .page-info {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .empty-state p {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .command-title {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .status-badge {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .filter-item label {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .header-text h2 {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .empty-state h3 {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .loading-section p {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .detail-item label {
  text-align: right;
  direction: rtl;
}

[dir=rtl] .detail-item code {
  text-align: left;
  direction: ltr;
}

[dir=rtl] .toggle-details {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .clear-filters-btn {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .refresh-btn {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .retry-btn {
  text-align: center;
  direction: rtl;
}

[dir=rtl] .page-btn {
  text-align: center;
  direction: rtl;
}

/* Responsive Design */
@media (max-width: 768px) {
  .history-container {
    padding: 16px;
  }

  .header {
    padding: 20px;
  }

  .header-content {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .header-main {
    text-align: center;
  }

  .header-main h2 {
    font-size: 1.3rem;
  }

  .header-main p {
    font-size: 0.9rem;
  }

  .refresh-toggle {
    padding: 8px 14px;
    font-size: 0.85rem;
  }

  .filter-row {
    grid-template-columns: 1fr;
  }

  .item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .status-section {
    align-items: flex-start;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  .command-text {
    max-width: 250px;
  }

  .pagination {
    flex-direction: column;
    align-items: center;
  }

  .page-numbers {
    order: 2;
  }

  .page-info {
    order: 3;
  }
}

@media (max-width: 480px) {
  .header {
    padding: 16px;
  }

  .header-main h2 {
    font-size: 1.2rem;
  }

  .header-main p {
    font-size: 0.85rem;
  }

  .refresh-toggle {
    padding: 8px 12px;
    font-size: 0.8rem;
  }

  .command-text {
    max-width: 200px;
  }

  .detail-grid {
    gap: 12px;
  }
}
</style>
